import { useCallback, useEffect, useState } from 'react';
import { notificationsApi } from './api';

/* Manages the admin's Web Push subscription lifecycle: permission prompt,
   PushManager subscribe/unsubscribe, and syncing the subscription with the
   backend. The service worker (public/sw.js) shows the actual notifications.

   The VAPID public key is fetched from the backend (GET /v1/admin/push/config)
   so the browser subscribes with the exact key the backend signs with — no
   silent 403 key mismatch, and no dependence on a build-time VITE_VAPID key
   that a CI build might omit. A build-time env key is only a last-resort
   fallback if the config endpoint is unreachable.

   State:
     unsupported — browser lacks SW/Push, or the backend has push disabled.
     denied      — the user blocked notifications at the OS/browser level.
     off / on    — subscription is currently inactive / active.
     busy        — a permission prompt or network call is in flight. */

export type PushState = 'unsupported' | 'denied' | 'off' | 'on' | 'busy';

/** Build-time fallback only — the backend config endpoint is authoritative. */
const FALLBACK_VAPID_KEY: string | undefined = import.meta.env.VITE_VAPID_PUBLIC_KEY;

/** Whether the browser has the APIs required for Web Push, independent of any
    key. Exported for unit tests. */
export function browserSupportsPush(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Convert a base64url VAPID key into the byte array PushManager expects.
    Exported for unit tests. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    bytes[i] = raw.charCodeAt(i);
  }
  return bytes;
}

export function usePushNotifications(): {
  state: PushState;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
} {
  const [state, setState] = useState<PushState>('busy');
  // Resolved at runtime from the backend; null until known (or if disabled).
  const [vapidKey, setVapidKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!browserSupportsPush()) {
        setState('unsupported');
        return;
      }
      // Ask the backend for its VAPID key. If push is disabled server-side the
      // bell stays hidden; if the endpoint is unreachable, fall back to the
      // build-time key so a transient blip doesn't hide the toggle.
      let key: string | null = null;
      try {
        const config = await notificationsApi.config();
        key = config.enabled ? config.vapid_public_key : null;
      } catch {
        key = FALLBACK_VAPID_KEY ?? null;
      }
      if (cancelled) return;
      if (!key) {
        setState('unsupported');
        return;
      }
      setVapidKey(key);
      if (Notification.permission === 'denied') {
        setState('denied');
        return;
      }
      // Guard the SW/subscription query: a rejection here must not leave the
      // hook stuck at the initial 'busy' (which renders the bell as a
      // permanently-disabled spinner). Default to 'off' — the toggle stays
      // usable and `enable()` self-registers the worker.
      let subscription: PushSubscription | null = null;
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        subscription = registration ? await registration.pushManager.getSubscription() : null;
      } catch {
        subscription = null;
      }
      if (cancelled) return;
      setState(subscription ? 'on' : 'off');
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const enable = useCallback(async () => {
    if (!browserSupportsPush() || !vapidKey) return;
    setState('busy');
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState(permission === 'denied' ? 'denied' : 'off');
        return;
      }
      // Ensure the worker is registered (it auto-registers in prod, but a
      // user may enable from a dev/preview session before that runs).
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      const json = subscription.toJSON();
      await notificationsApi.subscribe({
        endpoint: json.endpoint ?? '',
        p256dh: json.keys?.p256dh ?? '',
        auth: json.keys?.auth ?? '',
      });
      setState('on');
    } catch {
      setState('off');
    }
  }, [vapidKey]);

  const disable = useCallback(async () => {
    setState('busy');
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = registration ? await registration.pushManager.getSubscription() : null;
      if (subscription) {
        await notificationsApi.unsubscribe(subscription.endpoint).catch(() => undefined);
        await subscription.unsubscribe();
      }
      setState('off');
    } catch {
      setState('off');
    }
  }, []);

  return { state, enable, disable };
}
